import { CUSTOM_ELEMENTS_SCHEMA, ErrorHandler, LOCALE_ID, NgModule } from '@angular/core';
import { MAT_RIPPLE_GLOBAL_OPTIONS, RippleGlobalOptions } from '@angular/material/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

import { APP_BASE_HREF, registerLocaleData } from '@angular/common';
import localeDe from '@angular/common/locales/de';
import localeDeAT from '@angular/common/locales/de-AT';
import localeDeCH from '@angular/common/locales/de-CH';
import localeEn from '@angular/common/locales/en';
import localeEnGB from '@angular/common/locales/en-GB';
import localeEsMX from '@angular/common/locales/es-MX';
import localeFr from '@angular/common/locales/fr';
import localeFrCH from '@angular/common/locales/fr-CH';
import localeNl from '@angular/common/locales/nl';
import localeZhHans from '@angular/common/locales/zh-Hans';

registerLocaleData( localeDe, 'de' );
registerLocaleData( localeDeAT, 'de-AT' );
registerLocaleData( localeDeCH, 'de-CH' );
registerLocaleData( localeEn, 'en' );
registerLocaleData( localeEnGB, 'en-GB' );
registerLocaleData( localeEsMX, 'es-MX' );
registerLocaleData( localeFr, 'fr-FR' );
registerLocaleData( localeFrCH, 'fr-CH' );
registerLocaleData( localeNl, 'nl-NL' );
registerLocaleData( localeZhHans, 'zh-Hans' );

import * as Sentry from '@sentry/angular-ivy';
import { provideMatomo, withRouter } from 'ngx-matomo-client';
import { withFormAnalytics } from 'ngx-matomo-client/form-analytics';

import { environment } from 'src/environments/environment';

// Import the Azure AD B2C configuration
import { MSAL_GUARD_CONFIG, MSAL_INSTANCE, MSAL_INTERCEPTOR_CONFIG, MsalGuard, MsalGuardConfiguration, MsalInterceptor, MsalInterceptorConfiguration, MsalModule, MsalRedirectComponent, MsalService } from '@azure/msal-angular';
import { msalConfig, msalGuardConfig, msalInterceptorConfig } from './shared/auth-config';

import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi, withJsonpSupport } from '@angular/common/http';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';

// Common
import { FooterComponent } from './common/footer/footer.component';
import { LanguageSwitchComponent } from './common/language-switch/language-switch.component';
import { ToolbarComponent } from './common/toolbar/toolbar.component';
import { NotAllowedComponent } from './pages/not-allowed/not-allowed.component';
import { NotFoundComponent } from './pages/not-found/not-found.component';

// Components
import { LoadingComponent } from './common/loading/loading.component';
import { MatomoConsentComponent } from './common/matomo-consent/matomo-consent.component';
import { ScrollTopComponent } from './common/scroll-top/scroll-top.component';
import { AvatarSelectorComponent } from './components/avatar-selector/avatar-selector.component';
import { CardComponent } from './components/card/card.component';
import { ExchangeRateCalculatorComponent } from './components/exchange-rate-calculator/exchange-rate-calculator.component';
import { MapComponent } from './components/map/map.component';
import { NewsComponent } from './components/news/news.component';
import { HomeModule } from './modules/home/home.module';
import { HomeComponent } from './modules/home/home/home.component';
import { LaunchDialogComponent } from './modules/home/launch-dialog/launch-dialog.component';
import { LaunchScopeComponent } from './modules/home/launch-dialog/launch-scope/launch-scope.component';
import { RuntimeListComponent } from './modules/home/launch-dialog/runtime-list/runtime-list.component';
import { StartScopeComponent } from './modules/home/launch-dialog/start-scope/start-scope.component';
import { ScopeCardComponent } from './modules/home/scope-card/scope-card.component';
import { ProfileComponent } from './pages/profile/profile.component';
import { StyleGuideComponent } from './pages/styleguide/styleguide.component';

// Services
import { CookieService } from 'ngx-cookie-service';
import { AuthService } from './services/auth.service';
import { BackendService } from './services/backend.service';
import { LoadingService } from './services/loading.service';
import { LocaleService } from './services/locale.service';
import { NewsService } from './services/news.service';

// Shared Modules
import { SharedModule } from './shared/shared.module';

import { LoadingInterceptor } from './shared/interceptors/loading.interceptor';
import { TokenInterceptor } from './shared/interceptors/token.interceptor';

import { IPublicClientApplication, PublicClientApplication } from '@azure/msal-browser';
import { DebounceClickDirective } from './shared/directives/debounce-click.directive';

/**
 * MSAL instance factory
 * @returns client application
 */
export function MSALInstanceFactory(): IPublicClientApplication {
    return new PublicClientApplication( msalConfig );
}
/**
 * MSAL interceptor factory
 * @returns config
 */
export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
    return msalInterceptorConfig;
}
/**
 * MSAL guard factory
 * @returns config
 */
export function MSALGuardConfigFactory(): MsalGuardConfiguration {
    return msalGuardConfig;
}

/** disable ripple animation globally */
const globalRippleConfig: RippleGlobalOptions = {
    disabled: true,
    animation: {
        enterDuration: 0,
        exitDuration: 0
    }
};

@NgModule( {
    declarations: [
        AppComponent,
        CardComponent,
        ToolbarComponent,
        FooterComponent,
        NotAllowedComponent,
        NotFoundComponent,
        LanguageSwitchComponent,
        NewsComponent,
        MatomoConsentComponent,
        ProfileComponent,
        HomeComponent,
        LaunchDialogComponent,
        StartScopeComponent,
        LaunchScopeComponent,
        RuntimeListComponent,
        ScopeCardComponent,
        ScrollTopComponent,
        StyleGuideComponent
    ],
    exports: [AvatarSelectorComponent, MapComponent],
    bootstrap: [AppComponent, MsalRedirectComponent],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    imports: [
        BrowserModule,
        BrowserAnimationsModule,
        DebounceClickDirective,
        HomeModule,
        AvatarSelectorComponent,
        LoadingComponent,
        MapComponent,
        ExchangeRateCalculatorComponent,
        MsalModule,
        SharedModule,
        AppRoutingModule
    ],
    providers: [
        AuthService,
        BackendService,
        CookieService,
        LoadingService,
        NewsService,
        MsalService,
        MsalGuard,
        {
            provide: MSAL_INSTANCE,
            useFactory: MSALInstanceFactory
        },
        {
            provide: MSAL_GUARD_CONFIG,
            useFactory: MSALGuardConfigFactory
        },
        {
            provide: MSAL_INTERCEPTOR_CONFIG,
            useFactory: MSALInterceptorConfigFactory
        },
        { provide: HTTP_INTERCEPTORS, useClass: MsalInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: TokenInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: LoadingInterceptor, multi: true },
        {
            provide: LOCALE_ID,
            useFactory: ( localeService: LocaleService ) => localeService.locale,
            deps: [LocaleService]
        },
        { provide: APP_BASE_HREF, useValue: '/' },
        { provide: MAT_RIPPLE_GLOBAL_OPTIONS, useValue: globalRippleConfig },
        {
            provide: ErrorHandler,
            useValue: Sentry.createErrorHandler( {
                showDialog: false
            } )
        },
        provideMatomo(
            {
                acceptDoNotTrack: true,
                disabled: !environment.optionalModules.showMatomoConsent,
                requireConsent: 'tracking',
                siteId: environment.keys.matomo,
                trackerUrl: 'https://riege.matomo.cloud'
            },
            withRouter(),
            withFormAnalytics()
        ),
        provideHttpClient( withInterceptorsFromDi(), withJsonpSupport() )
    ]
} )
export class AppModule { }
